import { Img } from "@/components/ui/img";
import { cn } from "@/lib/utils";
import { Buildings, CaretRight, Star } from "@phosphor-icons/react";
import { getInitials } from "@/components/admin/payouts/payout-helpers";
import { formatDate } from "./helpers";
import type { Client } from "@/types";

function ClientAvatar({
  client,
  className,
}: {
  client: Client;
  className?: string;
}) {
  if (client.image) {
    return (
      <Img
        src={client.image}
        alt={client.name}
        className={cn("shrink-0 rounded-full object-cover", className)}
      />
    );
  }
  return (
    <div
      className={cn(
        "flex shrink-0 items-center justify-center rounded-full bg-primary/10 font-bold text-primary",
        className,
      )}
    >
      {getInitials(client.name)}
    </div>
  );
}

function StatusBadge({ client }: { client: Client }) {
  if (client.blockedAt) {
    return (
      <span className="shrink-0 rounded-full bg-rose-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-rose-700 ring-1 ring-rose-200">
        Blokiran
      </span>
    );
  }
  if (client.repeatCancelFlag) {
    return (
      <span
        className="shrink-0 rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-700 ring-1 ring-amber-200"
        title="Posljednje dvije seanse klijent je sam otkazao"
      >
        2× otkazao
      </span>
    );
  }
  return null;
}

function TypeBadge({ client }: { client: Client }) {
  if (client.companyName) {
    return (
      <span
        className="inline-flex max-w-full items-center gap-1.5 rounded-full bg-blue-50 px-2.5 py-1 text-xs font-medium text-blue-700 ring-1 ring-blue-500/20"
        title={client.companyName}
      >
        <Buildings className="size-3 shrink-0" />
        <span className="truncate">{client.companyName}</span>
      </span>
    );
  }
  return <span className="text-xs text-muted-foreground">Samostalni</span>;
}

function RatingCell({ rating }: { rating: number | null }) {
  if (rating == null) {
    return <span className="text-muted-foreground/50">—</span>;
  }
  return (
    <span className="inline-flex items-center gap-1 tabular-nums text-foreground">
      <Star weight="fill" className="size-3.5 text-amber-500" />
      {rating.toFixed(1)}
    </span>
  );
}

function sessionsTitle(client: Client): string {
  return `Završene: ${client.completedSessions} · Nadolazeće: ${client.upcomingSessions}`;
}

/* ─── Desktop table + mobile cards ─── */

export function ClientsTable({
  clients,
  onSelect,
}: {
  clients: Client[];
  onSelect: (client: Client) => void;
}) {
  return (
    <>
      {/* Desktop table */}
      <div className="hidden overflow-hidden rounded-2xl border border-border/60 bg-card shadow-sm lg:block">
        <table className="w-full table-fixed text-[13px]">
          <colgroup>
            <col className="w-[300px]" />
            <col />
            <col className="w-[88px]" />
            <col className="w-[88px]" />
            <col className="w-[124px]" />
            <col className="w-[56px]" />
          </colgroup>
          <thead>
            <tr className="bg-muted/40">
              <Th>Klijent</Th>
              <Th>Tip</Th>
              <Th align="right">Sesije</Th>
              <Th align="right">Ocjena</Th>
              <Th align="right">Registracija</Th>
              <th className="px-3 py-2.5" />
            </tr>
          </thead>
          <tbody>
            {clients.map((client, i) => (
              <tr
                key={client.id}
                onClick={() => onSelect(client)}
                className={cn(
                  "group cursor-pointer transition-colors hover:bg-primary/[0.03]",
                  i !== clients.length - 1 && "border-b border-border/40",
                )}
              >
                <td className="px-4 py-3.5">
                  <div className="flex items-center gap-3">
                    <ClientAvatar client={client} className="size-9 text-[11px]" />
                    <div className="min-w-0">
                      <div className="flex items-center gap-1.5">
                        <span className="truncate font-medium leading-tight text-foreground">
                          {client.name}
                        </span>
                        <StatusBadge client={client} />
                      </div>
                      <div className="mt-0.5 truncate text-[11px] text-muted-foreground">
                        {client.email}
                      </div>
                    </div>
                  </div>
                </td>
                <td className="px-3 py-3.5">
                  <TypeBadge client={client} />
                </td>
                <td
                  className="whitespace-nowrap px-3 py-3.5 text-right tabular-nums text-foreground"
                  title={sessionsTitle(client)}
                >
                  {client.sessionCount}
                </td>
                <td className="whitespace-nowrap px-3 py-3.5 text-right">
                  <RatingCell rating={client.avgRating} />
                </td>
                <td className="whitespace-nowrap px-3 py-3.5 text-right tabular-nums text-muted-foreground">
                  {formatDate(client.createdAt)}
                </td>
                <td className="py-3.5 pl-1 pr-4 text-right">
                  <button
                    type="button"
                    aria-label={`Detalji: ${client.name}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      onSelect(client);
                    }}
                    className="inline-flex size-7 items-center justify-center rounded-lg text-muted-foreground/50 transition-colors group-hover:text-muted-foreground hover:bg-muted hover:text-foreground"
                  >
                    <CaretRight className="size-4" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile/Tablet cards */}
      <div className="grid gap-3 sm:grid-cols-2 lg:hidden">
        {clients.map((client) => (
          <button
            key={client.id}
            type="button"
            onClick={() => onSelect(client)}
            className="group rounded-2xl border border-border/60 bg-card p-4 text-left shadow-sm transition-shadow hover:shadow-md"
          >
            <div className="flex items-start justify-between gap-3">
              <div className="flex min-w-0 items-center gap-3">
                <ClientAvatar client={client} className="size-11 text-xs" />
                <div className="min-w-0">
                  <div className="flex items-center gap-1.5">
                    <span
                      className="truncate text-sm font-semibold text-foreground"
                      style={{ fontFamily: "var(--font-heading)" }}
                    >
                      {client.name}
                    </span>
                    <StatusBadge client={client} />
                  </div>
                  <div className="mt-0.5 truncate text-xs text-muted-foreground">
                    {client.email}
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-3">
              <TypeBadge client={client} />
            </div>

            <div className="mt-3 flex items-center justify-between border-t border-border/40 pt-3 text-xs text-muted-foreground">
              <span className="inline-flex items-center gap-3">
                <span className="tabular-nums" title={sessionsTitle(client)}>
                  {client.sessionCount} sesija
                </span>
                {client.avgRating != null && (
                  <span className="inline-flex items-center gap-1 tabular-nums">
                    <Star weight="fill" className="size-3 text-amber-500" />
                    {client.avgRating.toFixed(1)}
                  </span>
                )}
              </span>
              <span className="inline-flex items-center gap-1 tabular-nums">
                {formatDate(client.createdAt)}
                <CaretRight className="size-3.5 transition-transform group-hover:translate-x-0.5" />
              </span>
            </div>
          </button>
        ))}
      </div>
    </>
  );
}

function Th({
  children,
  align = "left",
}: {
  children: React.ReactNode;
  align?: "left" | "right";
}) {
  return (
    <th
      className={cn(
        "px-3 py-2.5 text-[11px] font-semibold uppercase tracking-widest text-muted-foreground first:px-4",
        align === "right" ? "text-right" : "text-left",
      )}
    >
      {children}
    </th>
  );
}
